import { useCallback, useEffect, useRef } from 'react';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const useDraggablePlayhead = ({ currentTime, duration, onSeekTime, timelineRef }) => {
    const dragRef = useRef(null);
    const dragFrameRef = useRef(null);

    const getTimeFromClientX = useCallback(
        (clientX) => {
            const timeline = timelineRef.current;
            if (!timeline || !duration || !timeline.scrollWidth) return 0;
            const bounds = timeline.getBoundingClientRect();
            const progress = clamp((clientX - bounds.left + timeline.scrollLeft) / timeline.scrollWidth, 0, 1);
            return progress * duration;
        },
        [duration, timelineRef]
    );

    const flushDrag = useCallback(() => {
        const drag = dragRef.current;
        if (drag?.pendingTime === null || drag?.pendingTime === undefined) return;
        onSeekTime(drag.pendingTime);
        drag.pendingTime = null;
    }, [onSeekTime]);

    const cancelScheduledDrag = useCallback(() => {
        const frame = dragFrameRef.current;
        if (!frame) return;
        if (frame.usesAnimationFrame) window.cancelAnimationFrame(frame.id);
        else window.clearTimeout(frame.id);
        dragFrameRef.current = null;
    }, []);

    const scheduleDrag = useCallback(() => {
        if (dragFrameRef.current) return;
        const flush = () => {
            dragFrameRef.current = null;
            flushDrag();
        };
        if (typeof window.requestAnimationFrame === 'function') {
            dragFrameRef.current = { id: window.requestAnimationFrame(flush), usesAnimationFrame: true };
        } else {
            dragFrameRef.current = { id: window.setTimeout(flush, 0), usesAnimationFrame: false };
        }
    }, [flushDrag]);

    const startDrag = useCallback(
        (event) => {
            if (event.button !== 0 || event.isPrimary === false || dragRef.current) return;
            const time = getTimeFromClientX(event.clientX);
            dragRef.current = { pendingTime: null, pointerId: event.pointerId };
            event.currentTarget.setPointerCapture?.(event.pointerId);
            onSeekTime(time);
            event.preventDefault();
        },
        [getTimeFromClientX, onSeekTime]
    );

    const moveDrag = useCallback(
        (event) => {
            const drag = dragRef.current;
            if (!drag || drag.pointerId !== event.pointerId) return;
            drag.pendingTime = getTimeFromClientX(event.clientX);
            scheduleDrag();
            event.preventDefault();
        },
        [getTimeFromClientX, scheduleDrag]
    );

    const endDrag = useCallback(
        (event) => {
            const drag = dragRef.current;
            if (!drag || drag.pointerId !== event.pointerId) return;
            cancelScheduledDrag();
            flushDrag();
            dragRef.current = null;
            if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId);
            }
        },
        [cancelScheduledDrag, flushDrag]
    );

    const handleKeyDown = useCallback(
        (event) => {
            const step = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            const time =
                event.key === 'Home' ? 0 : event.key === 'End' ? duration : clamp(currentTime + step, 0, duration);
            event.preventDefault();
            onSeekTime(time);
        },
        [currentTime, duration, onSeekTime]
    );

    useEffect(() => {
        return () => {
            cancelScheduledDrag();
            dragRef.current = null;
        };
    }, [cancelScheduledDrag]);

    return {
        dragHandleProps: {
            onPointerDown: startDrag,
            onPointerMove: moveDrag,
            onPointerUp: endDrag,
            onPointerCancel: endDrag,
            onLostPointerCapture: endDrag,
        },
        handleKeyDown,
    };
};

export default useDraggablePlayhead;
